import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { categories, getProductsByCategory, Product } from '../data/menuData.ts';
import ProductModal from './ProductModal.tsx';
import LazyImage from './LazyImage.tsx';
import { SHOW_MENU_PRICES } from '../config/ui.ts';
import entradasImg from '../assets/entradas.jpg';
import combinadosImg from '../assets/combinados.jpg';
import arrocesImg from '../assets/arroces.jpg';
import cevichopsImg from '../assets/cevichops.jpg';
import cevicheraBarraImg from '../assets/cevicheriabarra.jpg';
import porcionesImg from '../assets/porciones.jpg';
import cervezasImg from '../assets/cervezas.jpg';
import coctelesImg from '../assets/cocteles.jpg';
import alguitoMasImg from '../assets/alguitomas.jpg';
import bebidasImg from '../assets/bebidas.JPG';
import postresImg from '../assets/postres.JPG';
import promocionesImg from '../assets/promociones.JPG';

gsap.registerPlugin(ScrollTrigger);

const MenuSectionContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, var(--white) 0%, rgba(211, 141, 84, 0.10) 100%);
  padding: 80px 0;
  position: relative;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: var(--navy-blue);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #000;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;


const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CategoryCard = styled.div`
  background: var(--white);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryImage = styled.div<{ bgColor: string; bgImage?: string }>`
  flex: 0 0 65%;
  background: ${props => props.bgImage ? `url(${props.bgImage})` : props.bgColor};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(1, 74, 144, 0.18), rgba(0, 0, 0, 0.05));
  }
`;

const CategoryContent = styled.div`
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 0;
`;

const CategoryName = styled.h3`
  font-size: 1.1rem;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  font-weight: 600;
  color: var(--navy-blue);
  margin-bottom: 6px;
  margin-top: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CategoryDescription = styled.p`
  color: rgba(211, 141, 84, 0.82);
  font-size: 0.85rem;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  font-weight: 400;
  line-height: 1.3;
  margin-bottom: 10px;
  margin-top: 0;
  flex: 1;
`;

const CategoryStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #eee;
  margin-top: auto;
`;

const ProductCount = styled.span`
  color: #999;
  font-size: 0.9rem;
`;

const ViewCategoryButton = styled.button`
  background: var(--primary-turquoise);
  color: #e3e3dd;
  border: none;
  padding: 6px 16px;
  border-radius: 12px;
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: var(--dark-turquoise);
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(58, 174, 171, 0.35);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ProductCard = styled.div`
  background: var(--white);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 380px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  flex: 0 0 65%;
  background: linear-gradient(45deg, rgba(211, 141, 84, 0.25), rgba(58, 174, 171, 0.18)),
              linear-gradient(135deg, rgba(211, 141, 84, 0.95), rgba(211, 141, 84, 0.65));
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--navy-blue);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductBadges = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PopularBadge = styled.span`
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: var(--white);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const SpicyBadge = styled.span<{ level: 'mild' | 'medium' | 'hot' }>`
  background: ${props => 
    props.level === 'hot' ? '#ff4757' :
    props.level === 'medium' ? '#ffa502' :
    '#2ed573'
  };
  color: var(--white);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ProductContent = styled.div`
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 0;
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ProductName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--navy-blue);
  line-height: 1.2;
  margin: 0;
`;

const ProductPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--brand-gold);
  white-space: nowrap;
`;

const ProductDescription = styled.p`
  color: #666;
  font-size: 0.8rem;
  line-height: 1.3;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BackButton = styled.button`
  background: var(--primary-turquoise);
  color: #e3e3dd;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 30px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    color: #e3e3dd;
  }
  
  &:hover {
    transform: translateX(-5px);
    background: var(--dark-turquoise);
    box-shadow: 0 6px 16px rgba(58, 174, 171, 0.35);
  }
`;

interface MenuSectionProps {
  selectedLocation?: string;
  onAddToCart?: (product: Product, options: any, quantity: number) => void;
}

const MenuSection: React.FC<MenuSectionProps> = ({ selectedLocation = "Cayma", onAddToCart }) => {
  const [view, setView] = useState<'categories' | 'products'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToSectionTop = () => {
    const el = sectionRef.current;
    if (!el) return;
    // offset por el header fijo
    const headerOffset = 90;
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const getCategoryColor = (index: number): string => {
    const colors = [
      'linear-gradient(135deg, #FFE5B4, #FFCC80)',
      'linear-gradient(135deg, #E1F5FE, #B3E5FC)',
      'linear-gradient(135deg, #FFF3E0, #FFCC80)',
      'linear-gradient(135deg, #F3E5F5, #E1BEE7)',
      'linear-gradient(135deg, #E8F5E8, #C8E6C9)',
      'linear-gradient(135deg, #FFF8E1, #FFECB3)',
      'linear-gradient(135deg, #FCE4EC, #F8BBD9)',
      'linear-gradient(135deg, #E0F2F1, #B2DFDB)',
      'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
      'linear-gradient(135deg, #F1F8E9, #DCEDC8)',
      'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
      'linear-gradient(135deg, #F9FBE7, #F0F4C3)'
    ];
    return colors[index % colors.length];
  };

  const getCategoryImage = (categoryId: string): string | undefined => {
    const chicharronesFromPublic = `${process.env.PUBLIC_URL}/chop.jpeg`;
    const map: Record<string, string> = {
      'entradas': entradasImg,
      'combinados-barrio': combinadosImg,
      'arroces': arrocesImg,
      'cevichops': cevichopsImg,
      'barra-cevichera': cevicheraBarraImg,
      'chicharrones': chicharronesFromPublic,
      'porciones': porcionesImg,
      'cervezas': cervezasImg,
      'cocteles': coctelesImg,
      'alguito-mas': alguitoMasImg,
      // Si agregas archivos para estas categorías, mapea aquí:
      'bebidas': bebidasImg,
      'postres': postresImg,
      'promociones': promocionesImg,
    };

    return map[categoryId];
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setView('products');
  };

  // Al cambiar de vista a productos, asegúrate de mostrar el inicio del segmento (no el final de la página)
  useEffect(() => {
    if (view !== 'products') return;
    const raf = requestAnimationFrame(() => {
      scrollToSectionTop();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedCategory]);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleAddToCart = (product: Product, options: any, quantity: number) => {
    if (onAddToCart) {
      onAddToCart(product, options, quantity);
    } else {
      console.log('Producto agregado al carrito:', {
        ...product,
        quantity,
        selectedOptions: options
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const getSpicyIcon = (level: string) => {
    switch (level) {
      case 'hot': return '🌶️🌶️🌶️';
      case 'medium': return '🌶️🌶️';
      case 'mild': return '🌶️';
      default: return '';
    }
  };

  const filteredProducts = selectedCategory ? getProductsByCategory(selectedCategory) : [];

  useEffect(() => {
    gsap.fromTo('.category-card',
      { opacity: 0, y: 30, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }, [view]);

  return (
    <MenuSectionContainer id="menu-catalog" ref={sectionRef}>
      <Container>
        <SectionHeader>
          <SectionTitle>NUESTRA CARTA</SectionTitle>
          <SectionSubtitle>
            Descubre nuestra variedad de platos preparados con la frescura del mar peruano
          </SectionSubtitle>
        </SectionHeader>

        {view === 'categories' ? (
          <CategoriesGrid>
            {categories.map((category, index) => {
              const productCount = getProductsByCategory(category.id).length;
              return (
                <CategoryCard
                  key={category.id}
                  className="category-card"
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <CategoryImage
                    bgColor={getCategoryColor(index)}
                    bgImage={getCategoryImage(category.id)}
                  />
                  <CategoryContent>
                    <CategoryName>{category.name}</CategoryName>
                    <CategoryDescription>{category.description}</CategoryDescription>
                    <CategoryStats>
                      <ProductCount>{productCount} productos</ProductCount>
                      <ViewCategoryButton>
                        Ver productos
                      </ViewCategoryButton>
                    </CategoryStats>
                  </CategoryContent>
                </CategoryCard>
              );
            })}
          </CategoriesGrid>
        ) : (
          <>
            <BackButton onClick={() => setView('categories')}>
              <i className="fas fa-arrow-left"></i>
              Volver a categorías
            </BackButton>
            
            <ProductsGrid>
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                >
                  <ProductImage>
                    <ProductBadges>
                      {product.popular && (
                        <PopularBadge>Popular</PopularBadge>
                      )}
                      {product.spicyLevel && (
                        <SpicyBadge level={product.spicyLevel}>
                          {getSpicyIcon(product.spicyLevel)}
                        </SpicyBadge>
                      )}
                    </ProductBadges>
                    <LazyImage
                      src={product.image}
                      fallbackSrc={`${process.env.PUBLIC_URL}/logo.png`}
                      alt={product.name}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      placeholder="fas fa-image"
                    />
                  </ProductImage>
                  
                  <ProductContent>
                    <ProductHeader>
                      <ProductName>{product.name}</ProductName>
                      {SHOW_MENU_PRICES && (
                        <ProductPrice>S/. {product.price.toFixed(2)}</ProductPrice>
                      )}
                    </ProductHeader>
                    
                    <ProductDescription>{product.description}</ProductDescription>
                  </ProductContent>
                </ProductCard>
              ))}
            </ProductsGrid>
          </>
        )}
      </Container>

      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
      />
    </MenuSectionContainer>
  );
};

export default MenuSection;